import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardDaysReq,
    GrowingRespListItem,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthDashboardRespCardProps {
    gfIdx: number;
}

const GrowthDashboardRespCard = ({ gfIdx }: GrowthDashboardRespCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [growingRespList, setGrowingRespList] = useState<
        GrowingRespListItem[]
    >([]);
    const [selectedDays, setSelectedDays] = useState<number>(14);

    // 생육호흡 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchGrowingRespInfo(gfIdx, selectedDays);
        }
    }, [gfIdx, selectedDays]);

    const fetchGrowingRespInfo = async (gfIdx: number, days: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardDaysReq = { gfIdx, days };
            const response = await api.getGrowingRespCardInfo(req);
            setGrowingRespList(response.data || []);
        } catch (error) {
            console.error('생육호흡 정보 조회 실패:', error);
            setGrowingRespList([]);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (
            !chartRef.current ||
            !growingRespList ||
            growingRespList.length === 0
        ) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
            return;
        }

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // 데이터 추출
        const categories = growingRespList.map(item => {
            return item.measurementDate;
        });

        const maintenanceRespData = growingRespList.map(
            item => item.maintenanceResp || 0,
        );
        const growthRespData = growingRespList.map(
            item => item.growthResp || 0,
        );

        const options = {
            series: [
                {
                    type: 'area',
                    name: '생장호흡',
                    data: growthRespData,
                },
                {
                    type: 'line',
                    name: '유지호흡',
                    data: maintenanceRespData,
                },
            ],
            chart: {
                type: 'line',
                height: 273,
                toolbar: {
                    show: false,
                },
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 7,
                    left: 0,
                    blur: 1,
                    opacity: 0.05,
                },
            },
            grid: {
                borderColor: '#f1f1f1',
                strokeDashArray: 3,
            },
            //colors: ['rgb(227, 84, 212)', 'var(--primary-color)'],
            fill: {
                type: ['gradient', 'solid'],
                gradient: {
                    opacityFrom: 0.23,
                    opacityTo: 0.23,
                    shadeIntensity: 0.3,
                },
            },
            plotOptions: {
                bar: {
                    columnWidth: '20%',
                    borderRadius: 2,
                },
            },
            stroke: {
                width: [1.5, 2],
                curve: ['smooth', 'smooth'],
                dashArray: [5, 0],
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                show: true,
                position: 'top',
            },
            yaxis: {
                show: true,
                title: {
                    text: 'kg[CH2O]/ha/day',
                },
                labels: {
                    show: true,
                    formatter: function (val: number) {
                        return val.toFixed(1);
                    },
                },
            },
            xaxis: {
                show: true,
                type: 'datetime',
                categories: categories,
                labels: {
                    formatter: function (value: string) {
                        const date = new Date(value);
                        const year = String(date.getFullYear()).slice(-2);
                        const month = ('0' + (date.getMonth() + 1)).slice(-2);
                        const day = ('0' + date.getDate()).slice(-2);
                        return `${year}.${month}.${day}`;
                    },
                },
                axisBorder: {
                    show: false,
                    color: 'rgba(119, 119, 142, 0.05)',
                    offsetX: 0,
                    offsetY: 0,
                },
            },
            tooltip: {
                x: {
                    format: 'yyyy-MM-dd',
                },
                y: {
                    formatter: function (val: number) {
                        return val.toFixed(2) + ' kg[CH2O]/ha/day';
                    },
                },
            },
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [growingRespList]);

    // 기간 선택 버튼 핸들러
    const handleDaysChange = (days: number) => {
        setSelectedDays(days);
    };

    return (
        <div className="custom-card card">
            <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                <div className="card-title mb-sm-0 mb-2">
                    유지·생장 호흡
                    <Tooltip
                        content="유지호흡은 작물이 생명을 유지하기 위해 필요한 기초 에너지 소비량이고, 생장호흡은 새로운 조직을 만들기 위해 사용되는 에너지입니다. 두 값의 균형을 통해 작물의 생육 효율을 평가할 수 있습니다."
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
                <div
                    aria-label="Basic example"
                    role="group"
                    className="flex-wrap btn-group"
                >
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 7 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(7)}
                    >
                        7일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 14 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(14)}
                    >
                        14일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 30 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(30)}
                    >
                        30일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === 60 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(60)}
                    >
                        60일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn ${selectedDays === -1 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(-1)}
                    >
                        전체
                    </button>
                </div>
            </div>
            <div className="card-body">
                {loading ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '292px' }}
                    >
                        <div
                            className="spinner-border text-success"
                            role="status"
                        >
                            <span className="visually-hidden">로딩중...</span>
                        </div>
                    </div>
                ) : growingRespList.length === 0 ? (
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: '292px' }}
                    >
                        <div className="text-center text-muted">
                            생육호흡 데이터가 없습니다
                            <br /> (콩 등의 특정작물에만 노출됩니다)
                        </div>
                    </div>
                ) : (
                    <div ref={chartRef}></div>
                )}
            </div>
        </div>
    );
};

export default GrowthDashboardRespCard;
